import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useSubscription } from './hooks/useSubscription';
import { isLaunched } from './config/features';
import { Toaster } from './components/ui/sonner';
import { Sidebar, Header } from './components/Layout';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import QuotesPage from './pages/QuotesPage';
import QuoteBuilderPage from './pages/QuoteBuilderPage';
import QuoteDetailPage from './pages/QuoteDetailPage';
import PipelinePage from './pages/PipelinePage';
import FollowUpsPage from './pages/FollowUpsPage';
import SettingsPage from './pages/SettingsPage';
import AISurveyPage from './pages/AISurveyPage';
import ClientDetailPage from './pages/ClientDetailPage';
import AssistenteOperativoPage from './pages/AssistenteOperativoPage';
import TicketsPage from './pages/TicketsPage';
import DDTPage from './pages/DDTPage';
import DDTBuilderPage from './pages/DDTBuilderPage';
import ProformaPage from './pages/ProformaPage';
import ProformaBuilderPage from './pages/ProformaBuilderPage';
import CreditNotePage from './pages/CreditNotePage';
import CreditNoteBuilderPage from './pages/CreditNoteBuilderPage';
import InvoicePage from './pages/InvoicePage';
import InvoiceBuilderPage from './pages/InvoiceBuilderPage';
import PurchasesPage from './pages/PurchasesPage';
import PurchaseBuilderPage from './pages/PurchaseBuilderPage';
import AccountingDashboard from './pages/AccountingDashboard';
import PassiveInvoicesPage from './pages/PassiveInvoicesPage';

// Protected Route wrapper
function ProtectedRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0033A0]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <AppLayout />;
}

// Public Route wrapper (redirects to dashboard if authenticated)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0033A0]"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// App Layout with Sidebar
function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// Feature Gate wrapper for routes requiring specific plan features
function FeatureGate({ feature, children }) {
  const { canAccess, planName } = useSubscription();

  // Not launched yet (e.g. accounting): the page does not exist for users
  if (!isLaunched(feature)) return <Navigate to="/dashboard" replace />;

  if (!canAccess(feature)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4" data-testid="feature-locked">
        <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mb-5">
          <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 text-amber-500" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
        </div>
        <h2 className="font-outfit text-xl font-semibold text-[#0A0F1A] mb-2">
          Funzionalità non inclusa nel tuo piano
        </h2>
        <p className="text-sm text-[#4A5568] max-w-md mb-6">
          Questa sezione richiede il piano <strong>Gestionale Completo</strong>. Il tuo piano attuale è <strong>{planName}</strong>.
        </p>
        <a href="/settings" className="inline-flex items-center gap-2 bg-[#0033A0] text-white font-semibold px-6 py-2.5 rounded-lg hover:bg-[#002880] transition-colors text-sm" data-testid="upgrade-cta">
          Aggiorna il Piano
        </a>
      </div>
    );
  }

  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Landing page (public) */}
          <Route path="/" element={<LandingPage />} />

          {/* Auth routes (redirect to dashboard if already logged in) */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/clients/:id" element={<ClientDetailPage />} />
            <Route path="/quotes" element={<QuotesPage />} />
            <Route path="/quotes/new" element={<QuoteBuilderPage />} />
            <Route path="/quotes/:id/edit" element={<QuoteBuilderPage />} />
            <Route path="/quotes/:id" element={<QuoteDetailPage />} />
            <Route path="/pipeline" element={<PipelinePage />} />
            <Route path="/follow-ups" element={<FollowUpsPage />} />
            <Route path="/ai-survey" element={<AISurveyPage />} />
            <Route path="/assistente" element={<AssistenteOperativoPage />} />
            <Route path="/tickets" element={<TicketsPage />} />
            <Route path="/ddt" element={<FeatureGate feature="ddt"><DDTPage /></FeatureGate>} />
            <Route path="/ddt/new" element={<FeatureGate feature="ddt"><DDTBuilderPage /></FeatureGate>} />
            <Route path="/ddt/:id/edit" element={<FeatureGate feature="ddt"><DDTBuilderPage /></FeatureGate>} />
            <Route path="/proforma" element={<FeatureGate feature="proforma"><ProformaPage /></FeatureGate>} />
            <Route path="/proforma/new" element={<FeatureGate feature="proforma"><ProformaBuilderPage /></FeatureGate>} />
            <Route path="/proforma/:id/edit" element={<FeatureGate feature="proforma"><ProformaBuilderPage /></FeatureGate>} />
            <Route path="/note-credito" element={<FeatureGate feature="credit_notes"><CreditNotePage /></FeatureGate>} />
            <Route path="/note-credito/new" element={<FeatureGate feature="credit_notes"><CreditNoteBuilderPage /></FeatureGate>} />
            <Route path="/note-credito/:id/edit" element={<FeatureGate feature="credit_notes"><CreditNoteBuilderPage /></FeatureGate>} />
            <Route path="/fatture" element={<FeatureGate feature="invoices"><InvoicePage /></FeatureGate>} />
            <Route path="/fatture/new" element={<FeatureGate feature="invoices"><InvoiceBuilderPage /></FeatureGate>} />
            <Route path="/fatture/:id/edit" element={<FeatureGate feature="invoices"><InvoiceBuilderPage /></FeatureGate>} />
            <Route path="/acquisti" element={<FeatureGate feature="purchases"><PurchasesPage /></FeatureGate>} />
            <Route path="/acquisti/new" element={<FeatureGate feature="purchases"><PurchaseBuilderPage /></FeatureGate>} />
            <Route path="/acquisti/:id/edit" element={<FeatureGate feature="purchases"><PurchaseBuilderPage /></FeatureGate>} />
            <Route path="/contabilita" element={<FeatureGate feature="accounting"><AccountingDashboard /></FeatureGate>} />
            <Route path="/fatture-passive" element={<FeatureGate feature="passive_invoices"><PassiveInvoicesPage /></FeatureGate>} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;
